import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import {
    ChevronDownIcon,
    CheckCircleIcon,
    ClockIcon,
    DocumentTextIcon,
    PaperClipIcon,
    PencilSquareIcon,
    UsersIcon,
    ArrowUpTrayIcon,
} from '@heroicons/react/24/outline';
import { DrawerCustom } from '../../../components/drawer/Drawer';
import { Button } from '../../../components/buttons/Button';
import { TextareaModern } from '../../../components/textareaModern';
import { Avatar } from '../../../components/avatar';
import { TaskAttachmentField } from './TaskAttachmentField';
import { TaskAttachmentList } from './TaskAttachmentCard';
import { TaskDetailInfo } from './TaskDetailInfo';
import { TaskGradeStars } from './TaskGradeStars';
import { useTaskAttachment } from '../../../hooks/useTaskAttachment';
import { connectSocket, onSocketEvent } from '../../../services/socket';
import {
    fetchTaskDetail,
    fetchTaskSubmissions,
    submitCourseTask,
    gradeCourseTaskSubmission,
} from '../../../redux/actions/tasks';
import { formatDateTime } from '../../../common/formatDateText';
import { capitalizarPalabras } from '../../../common/upperCaseWord';
import { asAttachmentList } from '../../../common/taskUploads';
import {
    TASK_PRIORITY_LABELS,
    TASK_WINDOW_LABELS,
    priorityBadgeClass,
    shouldShowTeacherTaskStatusBadge,
    statusBadgeClass,
    windowBadgeClass,
    teacherWindowLabel,
    teacherWindowBadgeClass,
    canTeacherEditTask,
    courseDisplayName,
    teacherDisplayName,
} from './taskLabels';
import { CourseFormSection } from '../components/CourseFormSection';
import './task-detail-drawer.css';

const SECTION_ICON_CLASS = 'h-5 w-5';
const SUBMISSION_COMMENT_MAX = 1000;
const TEACHER_COMMENT_MAX = 1000;

const submissionFiles = (submission) => (
    asAttachmentList(submission?.attachments?.length ? submission.attachments : submission?.attachment)
);

const isGraded = (submission) => (
    Boolean(submission) && Number.isInteger(submission.grade)
);

export const TaskDetailDrawer = ({ isOpen, onClose, taskId, courseId, onEdit, onSubmitted }) => {
    const dispatch = useDispatch();
    const { role } = useSelector((state) => state.tasks);
    const { courseSelected } = useSelector((state) => state.course);

    const [detail, setDetail] = useState(null);
    const [roster, setRoster] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [expandedStudent, setExpandedStudent] = useState(null);
    const [comment, setComment] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [gradeDrafts, setGradeDrafts] = useState({});
    const [savingGradeFor, setSavingGradeFor] = useState(null);
    const [gradeErrors, setGradeErrors] = useState({});
    const draftTaskIdRef = useRef(null);

    const { attachment, isUploading, startUpload, clearAttachment } = useTaskAttachment(courseId);

    const isTeacherView = role === 'teacher' || role === 'observer';
    const isTeacher = role === 'teacher';
    const canStudentSubmit = Boolean(
        !isTeacherView
        && detail?.canSubmit
        && !detail?.mySubmission
        && detail?.windowState !== 'expired',
    );
    const canTeacherEdit = Boolean(isTeacher && detail && canTeacherEditTask(detail));
    const isProcessing = submitting || Boolean(savingGradeFor) || isUploading;

    const clearSubmissionDraft = useCallback(() => {
        setComment('');
        setGradeDrafts({});
        setGradeErrors({});
        setExpandedStudent(null);
        clearAttachment();
    }, [clearAttachment]);

    const loadDetail = useCallback(async ({ silent = false } = {}) => {
        if (!taskId) return;
        if (!silent) {
            setLoading(true);
            setError(null);
        }
        try {
            const task = await dispatch(fetchTaskDetail(taskId));
            setDetail(task);
            if (task && isTeacherView) {
                const data = await dispatch(fetchTaskSubmissions(taskId));
                setRoster(data);
                const nextDrafts = {};
                (data?.roster || []).forEach(({ student, submission }) => {
                    if (!submission) return;
                    nextDrafts[student._id] = {
                        grade: Number.isInteger(submission.grade) ? submission.grade : null,
                        teacherComment: submission.teacherComment || '',
                    };
                });
                setGradeDrafts((prev) => {
                    // Conserva borradores locales de calificación al reabrir el mismo detalle.
                    if (draftTaskIdRef.current === String(taskId) && Object.keys(prev).length) {
                        return { ...nextDrafts, ...prev };
                    }
                    return nextDrafts;
                });
            }
        } catch (err) {
            if (!silent) {
                setError(err?.response?.data?.message || 'No se pudo cargar la tarea.');
            }
        } finally {
            if (!silent) setLoading(false);
        }
    }, [taskId, dispatch, isTeacherView]);

    useEffect(() => {
        if (!isOpen || !taskId) return;

        const sameDraft = draftTaskIdRef.current === String(taskId);
        if (sameDraft) {
            loadDetail({ silent: true });
            return;
        }

        draftTaskIdRef.current = String(taskId);
        setDetail(null);
        setRoster(null);
        setExpandedStudent(null);
        setComment('');
        setGradeDrafts({});
        setGradeErrors({});
        clearAttachment();
        loadDetail();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, taskId]);

    const handleDismiss = () => {
        if (isProcessing) return;
        onClose();
    };

    const handleCancel = () => {
        if (isProcessing) return;
        draftTaskIdRef.current = null;
        clearSubmissionDraft();
        setDetail(null);
        setRoster(null);
        onClose();
    };

    useEffect(() => {
        if (!isOpen || !taskId || !courseId) return undefined;

        let unsub = () => {};
        let mounted = true;

        const setup = async () => {
            try {
                await connectSocket();
                if (!mounted) return;

                unsub = onSocketEvent('task_update', (payload) => {
                    if (!mounted) return;
                    if (String(payload?.courseId) !== String(courseId)) return;
                    if (String(payload?.taskId) !== String(taskId)) return;

                    if (payload.action === 'updated' || payload.action === 'submitted' || payload.action === 'graded') {
                        loadDetail({ silent: true });
                    }
                });
            } catch {
                // El listado ya se sincroniza con useTaskUpdates; el detalle puede reabrirse.
            }
        };

        setup();

        return () => {
            mounted = false;
            try {
                unsub();
            } catch {
                // el socket pudo haberse cerrado al cambiar de curso
            }
        };
    }, [isOpen, taskId, courseId, loadDetail]);

    const handleSelectFile = async (file) => {
        const result = await startUpload(file);
        if (!result.ok && result.message) {
            toast.error(result.message);
        }
    };

    const handleSubmit = async () => {
        if (submitting || isUploading) return;

        if (!attachment?.payload && !comment.trim()) {
            toast.error('Adjunta un archivo o escribe un comentario para entregar.');
            return;
        }

        if (comment.length > SUBMISSION_COMMENT_MAX) {
            toast.error(`El comentario no puede superar ${SUBMISSION_COMMENT_MAX} caracteres.`);
            return;
        }

        setSubmitting(true);
        try {
            const submission = await dispatch(submitCourseTask(taskId, {
                attachment: attachment?.payload || null,
                comment: comment.trim(),
            }));
            setDetail((prev) => (prev ? { ...prev, mySubmission: submission, canSubmit: false } : prev));
            setComment('');
            clearAttachment();
            toast.success('Tu tarea fue entregada correctamente.');
            onSubmitted?.();
        } catch (err) {
            toast.error(err?.response?.data?.message || 'No se pudo entregar la tarea.');
            loadDetail();
        } finally {
            setSubmitting(false);
        }
    };

    const handleSaveGrade = async (studentId, submission) => {
        if (!isTeacher || savingGradeFor) return;
        if (isGraded(submission)) {
            toast.error('Esta entrega ya fue calificada.');
            return;
        }
        const draft = gradeDrafts[studentId] || {};
        if (!Number.isInteger(draft.grade)) {
            setGradeErrors((prev) => ({ ...prev, [studentId]: 'Elige una calificación de 1 a 10.' }));
            toast.error('Elige una calificación de 1 a 10.');
            return;
        }

        const teacherComment = String(draft.teacherComment || '');
        if (teacherComment.length > TEACHER_COMMENT_MAX) {
            setGradeErrors((prev) => ({
                ...prev,
                [studentId]: `El comentario no puede superar ${TEACHER_COMMENT_MAX} caracteres.`,
            }));
            toast.error(`El comentario no puede superar ${TEACHER_COMMENT_MAX} caracteres.`);
            return;
        }

        setSavingGradeFor(studentId);
        setGradeErrors((prev) => ({ ...prev, [studentId]: '' }));
        try {
            const updated = await dispatch(gradeCourseTaskSubmission(taskId, submission._id, {
                grade: draft.grade,
                teacherComment: teacherComment.trim(),
            }));
            setRoster((prev) => {
                if (!prev) return prev;
                return {
                    ...prev,
                    roster: prev.roster.map((row) => (
                        String(row.student._id) === String(studentId)
                            ? { ...row, submission: { ...row.submission, ...updated } }
                            : row
                    )),
                };
            });
            toast.success('Calificación guardada');
        } catch (err) {
            toast.error(err?.response?.data?.message || 'No se pudo guardar la calificación.');
        } finally {
            setSavingGradeFor(null);
        }
    };

    const renderStudentSection = () => {
        if (!detail) return null;

        if (detail.mySubmission) {
            const graded = isGraded(detail.mySubmission);
            return (
                <CourseFormSection
                    id="task-detail-my-submission"
                    title={graded ? 'Tu calificación' : 'Tu entrega'}
                    description={graded
                        ? 'Resultado de la revisión del profesor.'
                        : 'Ya enviaste esta tarea. Espera la calificación.'}
                    icon={<CheckCircleIcon className={SECTION_ICON_CLASS} />}
                >
                    <div className={`task-detail__status ${graded ? 'task-detail__status--success' : 'task-detail__status--success'}`}>
                        <div className="task-detail__status-head">
                            <CheckCircleIcon
                                className="task-detail__status-icon task-detail__status-icon--success"
                                aria-hidden="true"
                            />
                            <div>
                                <p className="task-detail__status-title">
                                    {graded ? 'Tarea calificada' : 'Tarea entregada'}
                                </p>
                                <p className="task-detail__status-date">
                                    Entregada: {formatDateTime(detail.mySubmission.submittedAt)}
                                </p>
                            </div>
                        </div>
                    </div>

                    <TaskAttachmentList attachments={submissionFiles(detail.mySubmission)} />

                    {detail.mySubmission.comment ? (
                        <p className="task-detail__comment-readonly">{detail.mySubmission.comment}</p>
                    ) : null}

                    {graded ? (
                        <div className="task-detail__grade-result">
                            <TaskGradeStars
                                value={detail.mySubmission.grade}
                                readOnly
                                label="Calificación"
                            />
                            {detail.mySubmission.gradedAt ? (
                                <p className="task-detail__status-date">
                                    Calificada: {formatDateTime(detail.mySubmission.gradedAt)}
                                </p>
                            ) : null}
                            {detail.mySubmission.teacherComment ? (
                                <p className="task-detail__comment-readonly">
                                    {detail.mySubmission.teacherComment}
                                </p>
                            ) : null}
                        </div>
                    ) : (
                        <p className="task-detail__submit-hint">El profesor todavía no califica esta entrega.</p>
                    )}
                </CourseFormSection>
            );
        }

        if (detail.windowState === 'expired') {
            return (
                <CourseFormSection
                    id="task-detail-expired"
                    title="Plazo cerrado"
                    description="Ya no se aceptan entregas para esta tarea."
                    icon={<ClockIcon className={SECTION_ICON_CLASS} />}
                >
                    <div className="task-detail__status task-detail__status--muted">
                        <div className="task-detail__status-head">
                            <ClockIcon
                                className="task-detail__status-icon task-detail__status-icon--muted"
                                aria-hidden="true"
                            />
                            <p className="task-detail__status-title">
                                La fecha límite de entrega ha terminado.
                            </p>
                        </div>
                    </div>
                </CourseFormSection>
            );
        }

        if (!canStudentSubmit) return null;

        return (
            <CourseFormSection
                id="task-detail-submit"
                title="Entregar tarea"
                description="Adjunta tu archivo o deja un comentario para tu profesor."
                icon={<ArrowUpTrayIcon className={SECTION_ICON_CLASS} />}
            >
                <TaskAttachmentField
                    label="Archivo de tu entrega"
                    staged={attachment}
                    isUploading={isUploading}
                    onSelectFile={handleSelectFile}
                    onClearStaged={clearAttachment}
                    disabled={submitting}
                />

                <TextareaModern
                    id="submission-comment"
                    name="submission-comment"
                    label="Comentario (opcional)"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Agrega un comentario para tu profesor…"
                    maxLength={SUBMISSION_COMMENT_MAX}
                    minRows={3}
                    disabled={submitting}
                />
            </CourseFormSection>
        );
    };

    const renderRoster = () => {
        if (!isTeacherView || !roster) return null;

        return (
            <CourseFormSection
                id="task-detail-roster"
                title="Entregas"
                description={`${roster.submittedCount} de ${roster.totalStudents} alumnos han entregado.`}
                icon={<UsersIcon className={SECTION_ICON_CLASS} />}
            >
                {roster.roster.length === 0 ? (
                    <p className="task-detail__submit-hint">El curso no tiene alumnos registrados.</p>
                ) : (
                    <ul className="task-detail__roster-list">
                        {roster.roster.map(({ student, submission }) => {
                            const studentId = student._id;
                            const expanded = expandedStudent === studentId;
                            const name = teacherDisplayName(student) || 'Alumno';
                            const graded = isGraded(submission);
                            const draft = gradeDrafts[studentId] || { grade: null, teacherComment: '' };

                            return (
                                <li key={studentId} className="task-detail__roster-item">
                                    <button
                                        type="button"
                                        onClick={() => submission && setExpandedStudent(expanded ? null : studentId)}
                                        className={`task-detail__roster-trigger${
                                            submission ? ' task-detail__roster-trigger--interactive' : ''
                                        }`}
                                        aria-expanded={submission ? expanded : undefined}
                                    >
                                        <Avatar
                                            size="md"
                                            firstName={student.firstName}
                                            lastName={student.lastName}
                                            className="task-detail__roster-avatar"
                                        />
                                        <span className="task-detail__roster-body">
                                            <span className="task-detail__roster-name">{name}</span>
                                            {submission ? (
                                                <span className="task-detail__roster-meta task-detail__roster-meta--done">
                                                    {graded
                                                        ? `Calificada: ${submission.grade}/10 · ${formatDateTime(submission.gradedAt || submission.submittedAt)}`
                                                        : `Entregada: ${formatDateTime(submission.submittedAt)}`}
                                                </span>
                                            ) : (
                                                <span className="task-detail__roster-meta">No entregada</span>
                                            )}
                                        </span>
                                        {submission ? (
                                            <ChevronDownIcon
                                                className={`task-detail__roster-chevron${
                                                    expanded ? ' task-detail__roster-chevron--open' : ''
                                                }`}
                                                aria-hidden="true"
                                            />
                                        ) : null}
                                    </button>

                                    {expanded && submission ? (
                                        <div className="task-detail__roster-detail">
                                            {student.matricula ? (
                                                <p className="task-detail__submit-hint m-0">
                                                    Matrícula: {student.matricula}
                                                </p>
                                            ) : null}

                                            {submissionFiles(submission).length ? (
                                                <TaskAttachmentList attachments={submissionFiles(submission)} />
                                            ) : (
                                                <p className="task-detail__submit-hint m-0">Sin archivo adjunto.</p>
                                            )}

                                            {submission.comment ? (
                                                <p className="task-detail__comment-readonly m-0">
                                                    {submission.comment}
                                                </p>
                                            ) : null}

                                            {isTeacher && !graded ? (
                                                <div className="task-detail__grade-form">
                                                    <TaskGradeStars
                                                        id={`grade-${studentId}`}
                                                        value={draft.grade}
                                                        onChange={(grade) => {
                                                            setGradeDrafts((prev) => ({
                                                                ...prev,
                                                                [studentId]: { ...draft, grade },
                                                            }));
                                                            setGradeErrors((prev) => ({ ...prev, [studentId]: '' }));
                                                        }}
                                                        required
                                                        error={gradeErrors[studentId] || null}
                                                        disabled={savingGradeFor === studentId}
                                                    />
                                                    <TextareaModern
                                                        id={`teacher-comment-${studentId}`}
                                                        name={`teacher-comment-${studentId}`}
                                                        label="Comentario para el alumno (opcional)"
                                                        value={draft.teacherComment}
                                                        onChange={(e) => setGradeDrafts((prev) => ({
                                                            ...prev,
                                                            [studentId]: { ...draft, teacherComment: e.target.value },
                                                        }))}
                                                        placeholder="Escribe un comentario para el alumno…"
                                                        maxLength={TEACHER_COMMENT_MAX}
                                                        minRows={3}
                                                        disabled={savingGradeFor === studentId}
                                                    />
                                                    <Button
                                                        variant="primary"
                                                        onClick={() => handleSaveGrade(studentId, submission)}
                                                        loading={savingGradeFor === studentId}
                                                        loadingLabel="Guardando…"
                                                    >
                                                        Guardar evaluación
                                                    </Button>
                                                </div>
                                            ) : graded ? (
                                                <div className="task-detail__grade-result">
                                                    <TaskGradeStars value={submission.grade} readOnly />
                                                    {submission.teacherComment ? (
                                                        <p className="task-detail__comment-readonly m-0">
                                                            {submission.teacherComment}
                                                        </p>
                                                    ) : null}
                                                </div>
                                            ) : null}
                                        </div>
                                    ) : null}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </CourseFormSection>
        );
    };

    const teacherAttachments = asAttachmentList(
        detail?.attachments?.length ? detail.attachments : detail?.attachment,
    );

    return (
        <DrawerCustom
            isOpen={isOpen}
            toggleDrawer={handleDismiss}
            lockClose={isProcessing}
            title={detail?.title ? capitalizarPalabras(detail.title) : 'Detalle de tarea'}
            subtitle={capitalizarPalabras(courseDisplayName(courseSelected))}
            footer={(
                <div className="task-detail__footer">
                    <div className="task-detail__footer-actions">
                        <Button
                            variant="outline"
                            onClick={handleCancel}
                            disabled={isProcessing}
                        >
                            Cancelar
                        </Button>
                        {canTeacherEdit ? (
                            <Button
                                variant="outline"
                                icon={<PencilSquareIcon className="w-4 h-4" aria-hidden="true" />}
                                onClick={() => onEdit?.(detail)}
                                disabled={isProcessing}
                            >
                                Editar tarea
                            </Button>
                        ) : null}
                        {canStudentSubmit ? (
                            <Button
                                variant="primary"
                                className="task-detail__submit-btn"
                                onClick={handleSubmit}
                                loading={submitting}
                                loadingLabel="Entregando…"
                                disabled={isUploading}
                            >
                                Entregar tarea
                            </Button>
                        ) : null}
                    </div>
                </div>
            )}
        >
            <div className="task-detail">
                <div className="task-detail__scroll">
                    {loading ? (
                        <div className="task-detail__skeleton" aria-label="Cargando tarea">
                            <div className="task-detail__skeleton-badges" aria-hidden="true">
                                <span className="task-detail__skeleton-pill" />
                                <span className="task-detail__skeleton-pill task-detail__skeleton-pill--wide" />
                            </div>
                            <section className="task-detail__card task-detail__skeleton-card">
                                <span className="task-detail__skeleton-label" aria-hidden="true" />
                                <div className="task-detail__skeleton-description" aria-hidden="true">
                                    <span className="task-detail__skeleton-line task-detail__skeleton-line--full" />
                                    <span className="task-detail__skeleton-line task-detail__skeleton-line--full" />
                                    <span className="task-detail__skeleton-line task-detail__skeleton-line--lg" />
                                </div>
                            </section>
                        </div>
                    ) : error ? (
                        <div className="task-detail__error">
                            <p className="task-detail__error-text">{error}</p>
                            <Button variant="outline" onClick={loadDetail}>Reintentar</Button>
                        </div>
                    ) : detail ? (
                        <div className="task-detail__content">
                            <div className="task-detail__badges">
                                <span className={isTeacherView
                                    ? teacherWindowBadgeClass(detail.windowState)
                                    : windowBadgeClass(detail.windowState)}
                                >
                                    {isTeacherView
                                        ? teacherWindowLabel(detail.windowState)
                                        : TASK_WINDOW_LABELS[detail.windowState]}
                                </span>
                                {isTeacherView && shouldShowTeacherTaskStatusBadge(detail.status) ? (
                                    <span className={statusBadgeClass(detail.status)}>
                                        En revisión
                                    </span>
                                ) : null}
                                <span className={priorityBadgeClass(detail.priority)}>
                                    Prioridad: {TASK_PRIORITY_LABELS[detail.priority]}
                                </span>
                            </div>

                            <CourseFormSection
                                id="task-detail-description"
                                title="Descripción"
                                description="Consigna y detalles de la actividad."
                                icon={<DocumentTextIcon className={SECTION_ICON_CLASS} />}
                            >
                                <p className="task-detail__description">{detail.description}</p>
                            </CourseFormSection>

                            {teacherAttachments.length ? (
                                <CourseFormSection
                                    id="task-detail-teacher-files"
                                    title="Archivos del profesor"
                                    description="Material de apoyo de esta tarea."
                                    icon={<PaperClipIcon className={SECTION_ICON_CLASS} />}
                                >
                                    <TaskAttachmentList attachments={teacherAttachments} />
                                </CourseFormSection>
                            ) : null}

                            <TaskDetailInfo
                                detail={detail}
                                isTeacher={isTeacherView}
                            />

                            {isTeacherView ? renderRoster() : renderStudentSection()}
                        </div>
                    ) : null}
                </div>
            </div>
        </DrawerCustom>
    );
};
